import { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area } from "recharts";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { StatCard, inputCls, F, fmtINR } from "./CrmShared";

const COLORS = ["#7c6cf0", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b"];

export default function CrmReports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [r, setR] = useState(null);

  const load = useCallback(() => {
    api.get("/store-admin/crm/reports", { params: { date_from: from, date_to: to } })
      .then((res) => setR(res.data)).catch((e) => toast.error(errMsg(e)));
  }, [from, to]);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="crm-reports">
      <div className="flex flex-wrap items-end gap-2">
        <F label="From"><input type="date" data-testid="report-from" className={`${inputCls} w-auto`} value={from} onChange={(e) => setFrom(e.target.value)} /></F>
        <F label="To"><input type="date" data-testid="report-to" className={`${inputCls} w-auto`} value={to} onChange={(e) => setTo(e.target.value)} /></F>
        <button onClick={load} data-testid="report-apply" className="h-10 px-5 rounded-xl bg-foreground text-background text-xs font-extrabold">Apply</button>
      </div>

      {!r ? <div className="h-40 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-border border-t-foreground animate-spin" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Leads (filtered)" value={r.total_leads} testid="rep-total" />
            <StatCard label="Conversion Rate" value={`${r.conversion}%`} testid="rep-conversion" />
            <StatCard label="Total Lead Value" value={fmtINR(r.total_value)} testid="rep-value" />
            <StatCard label="Follow-ups Done" value={`${r.followup.completed}/${r.followup.scheduled}`} sub={`${r.followup.overdue} overdue now`} testid="rep-followup" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-background p-5">
              <h3 className="font-display font-bold mb-4">Leads by Source</h3>
              <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                <BarChart data={r.by_source} layout="vertical"><XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} /><Tooltip /><Bar dataKey="value" fill="#7c6cf0" radius={[0, 6, 6, 0]} /></BarChart>
              </ResponsiveContainer></div>
            </div>
            <div className="rounded-2xl border border-border bg-background p-5">
              <h3 className="font-display font-bold mb-4">Leads by Stage</h3>
              <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                <BarChart data={r.by_stage}><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} /><Tooltip /><Bar dataKey="value" fill="#0ea5e9" radius={[6, 6, 0, 0]} /></BarChart>
              </ResponsiveContainer></div>
            </div>
            <div className="rounded-2xl border border-border bg-background p-5">
              <h3 className="font-display font-bold mb-4">Leads by Date</h3>
              <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                <AreaChart data={r.by_date}><XAxis dataKey="date" tick={{ fontSize: 9 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} /><Tooltip /><Area dataKey="count" stroke="#7c6cf0" fill="#7c6cf022" strokeWidth={2} /></AreaChart>
              </ResponsiveContainer></div>
            </div>
            <div className="rounded-2xl border border-border bg-background p-5">
              <h3 className="font-display font-bold mb-4">Won vs Lost</h3>
              <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={r.won_lost} dataKey="count" nameKey="name" innerRadius={50} outerRadius={80} label>
                  {r.won_lost.map((e, i) => <Cell key={e.name} fill={i === 0 ? "#10b981" : "#ef4444"} />)}</Pie><Tooltip /><Legend /></PieChart>
              </ResponsiveContainer></div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-3">
            <StatCard label="Pipeline Value (open)" value={fmtINR(r.pipeline.pending)} testid="rep-pipe-pending" />
            <StatCard label="Won Value" value={fmtINR(r.pipeline.won)} tone="text-emerald-600" testid="rep-pipe-won" />
            <StatCard label="Lost Value" value={fmtINR(r.pipeline.lost)} tone="text-red-500" testid="rep-pipe-lost" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-background p-5" data-testid="rep-user-wise">
              <h3 className="font-display font-bold mb-3">User-wise Performance</h3>
              {r.user_wise.length === 0 ? <p className="text-sm text-muted-foreground">No data.</p> : (
                <div className="space-y-1">
                  <div className="grid grid-cols-4 text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground pb-2 border-b border-border">
                    <span>User</span><span>Leads</span><span>Won</span><span>Won value</span>
                  </div>
                  {r.user_wise.map((u) => (
                    <div key={u.user} className="grid grid-cols-4 text-sm py-2 border-b border-border last:border-0">
                      <span className="font-bold truncate">{u.user}</span><span>{u.leads}</span>
                      <span className="text-emerald-600 font-bold">{u.won}</span><span className="font-bold">{fmtINR(u.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-border bg-background p-5" data-testid="rep-monthly">
              <h3 className="font-display font-bold mb-3">Monthly Growth</h3>
              {r.monthly.length === 0 ? <p className="text-sm text-muted-foreground">No data.</p> : (
                <div className="space-y-1">
                  {r.monthly.map((m) => (
                    <div key={m.month} className="flex justify-between text-sm py-2 border-b border-border last:border-0">
                      <span className="font-bold">{m.month}</span>
                      <span>{m.leads} leads · <span className="text-emerald-600 font-bold">{fmtINR(m.won_value)}</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
